import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Shield, Eye, Wrench, Pencil, Trash2, ArrowLeft, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useUsersStore } from '@/stores/usersStore';
import { fetchUsers, fetchCustomers, deleteUser } from '@/api/users';
import TopBar from '@/components/layout/TopBar';
import UserFormModal from '@/components/admin/users/UserFormModal';
import type { ManagedUser } from '@/types/users';

const ROLE_CONFIG: Record<string, { label: string; color: string; icon: typeof Shield }> = {
  admin: { label: 'Admin', color: 'text-accent-purple bg-accent-purple/15', icon: Shield },
  operator: { label: 'Operator', color: 'text-accent-blue bg-accent-blue/15', icon: Wrench },
  viewer: { label: 'Viewer', color: 'text-text-muted bg-bg-tertiary', icon: Eye },
};

const PAGE_SIZE = 8;

function scopeSummary(user: ManagedUser): string {
  if (user.scopes.length === 0) return 'Sin acceso';
  if (user.scopes.some((s) => s.type === 'all')) return 'Todo';
  const clients = user.scopes.filter((s) => s.type === 'client').map((s) => s.value);
  if (clients.length === 0) return 'Sin acceso';
  if (clients.length <= 2) return clients.join(', ');
  return `${clients[0]}, ${clients[1]} +${clients.length - 2}`;
}

function searchableText(user: ManagedUser): string {
  const parts = [
    user.display_name,
    user.username,
    user.email,
    user.notification_email,
    user.role,
    user.is_temporary ? 'temporal' : 'activo',
    scopeSummary(user),
    ...user.scopes.filter((s) => s.type === 'client').map((s) => s.value),
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const {
    users, loading, error, isFormOpen,
    setUsers, setCustomers, setLoading, setError, openCreate, openEdit,
  } = useUsersStore();
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Redirect non-admin
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, customersData] = await Promise.all([
        fetchUsers(true),
        fetchCustomers(),
      ]);
      setUsers(usersData);
      setCustomers(customersData);
    } catch (err: any) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = async (userId: number) => {
    try {
      await deleteUser(userId);
      setConfirmDelete(null);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al desactivar');
    }
  };

  // Filter users by search term
  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter((u) => searchableText(u).includes(q));
  }, [users, search]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageUsers = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  if (currentUser?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-bg-primary">
      <TopBar />
      <div className="pt-14">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-9 h-9 bg-bg-tertiary border border-border-subtle rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:border-border-strong transition-all"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary">
                Gestión de Usuarios
              </h1>
              <p className="text-xs text-text-muted mt-0.5">
                {users.length} usuario{users.length !== 1 ? 's' : ''} registrado{users.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="h-9 px-4 bg-accent-purple text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2"
          >
            <UserPlus size={15} />
            Crear Usuario
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, usuario, rol, email, cliente..."
            className="w-full h-10 pl-9 pr-3 bg-bg-secondary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-blue/50 transition-colors"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-bg-secondary border border-border-strong rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left px-4 py-3 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Usuario
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Rol
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Notificaciones
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Acceso
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Estado
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-muted text-sm">
                    Cargando...
                  </td>
                </tr>
              ) : pageUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-text-muted text-sm">
                    {search ? 'Sin resultados para esa búsqueda' : 'No hay usuarios'}
                  </td>
                </tr>
              ) : (
                pageUsers.map((u) => {
                  const roleConf = ROLE_CONFIG[u.role] || ROLE_CONFIG.viewer;
                  const RoleIcon = roleConf.icon;
                  const isCurrentUser = String(u.id) === String(currentUser?.id);

                  return (
                    <tr
                      key={u.id}
                      className="border-b border-border-subtle/50 hover:bg-bg-tertiary/50 transition-colors"
                    >
                      {/* User info */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-bg-tertiary border border-border-subtle flex items-center justify-center text-text-muted text-xs font-bold uppercase">
                            {(u.display_name || u.username || '?')[0]}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-text-primary">
                              {u.display_name}
                            </div>
                            <div className="text-xs text-text-muted">
                              @{u.username || u.email || '—'}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${roleConf.color}`}
                        >
                          <RoleIcon size={12} />
                          {roleConf.label}
                        </span>
                      </td>

                      {/* Notification Email */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-secondary truncate max-w-[180px] block">
                          {u.notification_email || <span className="text-text-muted italic">—</span>}
                        </span>
                      </td>

                      {/* Scope */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-secondary">
                          {scopeSummary(u)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                            u.is_temporary
                              ? 'text-yellow-400'
                              : 'text-accent-green'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              u.is_temporary ? 'bg-yellow-400' : 'bg-accent-green'
                            }`}
                          />
                          {u.is_temporary ? 'Temporal' : 'Activo'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(u)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-accent-blue hover:bg-accent-blue/10 transition-all"
                            title="Editar"
                          >
                            <Pencil size={14} />
                          </button>
                          {!isCurrentUser && (
                            <>
                              {confirmDelete === u.id ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleDelete(u.id)}
                                    className="px-2 py-1 text-xs text-red-400 bg-red-500/10 rounded hover:bg-red-500/20 transition-colors"
                                  >
                                    Confirmar
                                  </button>
                                  <button
                                    onClick={() => setConfirmDelete(null)}
                                    className="px-2 py-1 text-xs text-text-muted hover:text-text-primary transition-colors"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setConfirmDelete(u.id)}
                                  className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-accent-red hover:bg-accent-red/10 transition-all"
                                  title="Desactivar"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
              <span className="text-xs text-text-muted">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}
                {search && ` (filtrado${filtered.length !== 1 ? 's' : ''})`}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-bg-tertiary disabled:opacity-30 disabled:pointer-events-none transition-all"
                >
                  <ChevronLeft size={15} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-all ${
                      p === safePage
                        ? 'bg-accent-blue/15 text-accent-blue'
                        : 'text-text-muted hover:text-text-primary hover:bg-bg-tertiary'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-bg-tertiary disabled:opacity-30 disabled:pointer-events-none transition-all"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      </div>

      {/* Modal */}
      {isFormOpen && <UserFormModal onSaved={loadData} />}
    </div>
  );
}
